import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, ElementRef, Optional, Component, ChangeDetectionStrategy, Self, Input, Output, ViewChild, HostBinding, forwardRef, Inject, InjectionToken, NgModule, Injectable } from '@angular/core';
import * as i1 from '@angular/forms';
import { ControlContainer, NgForm, NgModel, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import * as i6 from '@angular/material/autocomplete';
import { MatAutocomplete, MatAutocompleteTrigger, MatAutocompleteModule } from '@angular/material/autocomplete';
import * as i4$1 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i1$3 from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import * as i8 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import * as i5 from '@firestitch/clear';
import { FsClearModule } from '@firestitch/clear';
import * as i2$1 from '@firestitch/dialog';
import { FsDialogModule } from '@firestitch/dialog';
import * as i9 from '@firestitch/form';
import { FsFormModule } from '@firestitch/form';
import * as i2 from '@firestitch/map';
import { FsMapComponent, FsMapModule } from '@firestitch/map';
import * as i4 from '@angular/material/form-field';
import { MatFormFieldControl } from '@angular/material/form-field';
import * as i3 from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { guid } from '@firestitch/common';
import { Subject, fromEvent, of, bindCallback, Observable } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { controlContainerFactory } from '@firestitch/core';
import * as i7 from '@angular/material/core';
import { isObject, cloneDeep } from 'lodash-es';
import * as i1$2 from '@firestitch/autocomplete';
import { FsAutocompleteComponent, FsAutocompleteModule } from '@firestitch/autocomplete';

var AddressFormat;
(function (AddressFormat) {
    AddressFormat["OneLine"] = "oneline";
    AddressFormat["TwoLine"] = "twoline";
    AddressFormat["Summary"] = "summary";
})(AddressFormat || (AddressFormat = {}));

function addressIsEmpty(value) {
    return !value
        || (!value.name
            && !value.street
            && !value.city
            && !value.region
            && !value.zip
            && !value.country);
}

function createEmptyAddress() {
    return {
        name: '',
        description: '',
        country: '',
        region: '',
        city: '',
        street: '',
        address2: '',
        address3: '',
        zip: '',
        lat: null,
        lng: null,
    };
}

function extractUnit(text) {
    const primaryUnitRegex = /((unit|apt|#|apartment|building|floor|suite|room|department|po\s*box)\s?#?\d+([,.])?(\w)?([,.])?)/gi;
    const secondaryUnitRegex = /-\s?\d+/gi;
    const nonWordOrDigitChar = /^[^a-z\d]*|[^a-z\d]*$/gi;
    let unit = [
        ...(text.match(primaryUnitRegex) || []),
        ...(text.match(secondaryUnitRegex) || []),
    ][0];
    if (unit) {
        text = text
            .replace(unit, '')
            .trim();
        unit = unit
            .replace(nonWordOrDigitChar, '')
            .replace('unit', 'Unit')
            .trim();
    }
    text = text.replace(nonWordOrDigitChar, '').trim();
    return {
        text,
        unit,
    };
}

function googleDetailsToAddress(result, config) {
    const address = createEmptyAddress();
    let countryLongName, regionLongName, streetShortName;
    address.lat = result.geometry.location.lat();
    address.lng = result.geometry.location.lng();
    // Finding different parts of address
    result.address_components.forEach((item) => {
        if (item.types.some(type => type === 'country')) {
            address.country = item.short_name;
            countryLongName = item.long_name;
        }
        if (item.types.some(type => type === 'administrative_area_level_1')) {
            address.region = item.short_name;
            regionLongName = item.long_name;
        }
        if (item.types.some(type => type === 'locality')) {
            address.city = item.long_name;
        }
        if (item.types.some(type => type === 'postal_code')) {
            address.zip = item.long_name;
        }
    });
    // Address.Street consists from number and street
    const streetNumber = result.address_components
        .find(el => el.types.some(type => type === 'street_number'));
    if (streetNumber) {
        address.street = streetNumber.long_name + ' ';
        streetShortName = streetNumber.long_name + ' ';
    }
    else {
        const match = address.description.match(/^[\d-]+/);
        if (match) {
            address.street = match[0] + ' ';
            streetShortName = match[0] + ' ';
        }
    }
    const streetAddress = result.address_components
        .find(el => el.types.some(type => type === 'route'));
    if (streetAddress) {
        if (!address.street) {
            address.street = streetAddress.long_name;
            streetShortName = streetAddress.short_name;
        }
        else {
            address.street += streetAddress.long_name;
            streetShortName += streetAddress.short_name;
        }
    }
    // Checking correct place NAME
    if (address.country !== result.name
        && countryLongName !== result.name
        && address.region !== result.name
        && regionLongName !== result.name
        && address.city !== result.name
        && streetShortName !== result.name
        && address.zip !== result.name
        && address.street !== result.name) {
        if (config.name && config.name.visible !== false) {
            address.name = result.name;
        }
    }
    else {
        address.name = '';
    }
    return address;
}

class FsAddressAutocompleteComponent {
    constructor(ngControl, _map, _ngZone, _fm, _elementRef, _cdRef) {
        this.ngControl = ngControl;
        this._map = _map;
        this._ngZone = _ngZone;
        this._fm = _fm;
        this._elementRef = _elementRef;
        this._cdRef = _cdRef;
        this.format = AddressFormat.TwoLine;
        this.readonly = false;
        this.showClear = true;
        this.suggestions = false;
        this.addressChange = new EventEmitter();
        this.id = `fs-address-autocomplete-${FsAddressAutocompleteComponent.nextId++}`;
        this.inputAddress = this._defaultInputAddress();
        this.predictions = [];
        this.googleAutocompleteService = null;
        this.googlePlacesService = null;
        this.onChange = (data) => { };
        this.onTouched = () => { };
        this.errorState = false;
        this.focused = false;
        this.stateChanges = new Subject();
        this.autocompleteName = `search-${guid('xxxxxxxx')}`;
        this._config = {};
        this._address = {};
        this._searchText = '';
        this._disabled = false;
        this._required = false;
        this._destroy$ = new Subject();
        this.displayWith = (value) => {
            if (value && typeof value === 'object') {
                return this.value?.street;
            }
            else if (!this.empty) {
                return '';
            }
        };
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }
    set config(value) {
        this._config = value;
        if (this._config) {
            this.required =
                ((this.config.name && this.config.name.required) ||
                    (this.config.country && this.config.country.required) ||
                    (this.config.region && this.config.region.required) ||
                    (this.config.city && this.config.city.required) ||
                    (this.config.street && this.config.street.required) ||
                    (this.config.address2 && this.config.address2.required) ||
                    (this.config.address3 && this.config.address3.required) ||
                    (this.config.zip && this.config.zip.required));
        }
    }
    get config() {
        return this._config;
    }
    set value(value) {
        this._address = value;
        this.onChange(this._address);
    }
    get value() {
        return this._address;
    }
    get searchInputValue() {
        return this.searchElement.nativeElement.value;
    }
    get disabled() { return this._disabled; }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = coerceBooleanProperty(req);
        this.stateChanges.next();
    }
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next();
    }
    get shouldLabelFloat() {
        return this.focused;
    }
    get empty() {
        return addressIsEmpty(this.value);
    }
    ngOnInit() {
        this._initGoogleMap();
        this._listenUserTyping();
        this._listenAutocompleteSelection();
        this._registerFocusMonitor();
        this.ngControl.control.setValidators([this.validate.bind(this)]);
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    writeValue(value) {
        this._address = value;
        this.inputAddress = value;
        this._cdRef.markForCheck();
    }
    setDescribedByIds(ids) {
        // TODO
    }
    onContainerClick(event) {
        if (event.target.tagName.toLowerCase() != 'input') {
            this.searchElement.nativeElement.focus();
            this._elementRef.nativeElement.querySelector('input').focus();
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    validate(control) {
        const validationErrors = {};
        const requiredField = [];
        const parts = ['name', 'street', 'city', 'region', 'zip', 'country'];
        if (this.required && this.empty) {
            validationErrors.required = true;
        }
        if (!this.empty) {
            parts.forEach(part => {
                if (this.config[part] && this.config[part].required && !this.value[part]) {
                    requiredField.push([part]);
                }
            });
            if (((this.config.lat && this.config.lat.required) ||
                (this.config.lng && this.config.lng.required)) &&
                (!this.value.lat || !this.value.lat)) {
                validationErrors.invalid = 'position on map';
            }
            if (requiredField.length) {
                if (requiredField.length === 1) {
                    validationErrors.invalid = `The ${requiredField[0]} is required`;
                }
                else {
                    const last = requiredField.pop();
                    validationErrors.invalid = `The ${requiredField.join(', ')} and ${last} are required`;
                }
            }
        }
        return validationErrors;
    }
    clear() {
        this.inputAddress = this._defaultInputAddress();
        this.value = createEmptyAddress();
        this.ngControl?.control.setValue(this.value);
        this._clearPredictions();
        setTimeout(() => {
            this.autocompleteTrigger.openPanel();
        });
    }
    reset() {
        this.ngControl.reset(createEmptyAddress());
    }
    autocompletePanelClosed() {
        if (this.empty && !!this.inputAddress) {
            this.inputAddress = null;
        }
    }
    // Search input can't be null. We implemented required validation to show asterisk if needed
    // But general validation placed in another level and not depends of this input
    // This hack allow us to show asterisk but disable extra validation
    _defaultInputAddress() {
        return null;
    }
    _listenUserTyping() {
        this._ngZone.runOutsideAngular(() => {
            fromEvent(this.searchElement.nativeElement, 'keydown')
                .pipe(filter((event) => event.code === 'Tab'), map(() => this.autocompleteTrigger.activeOption?.value), filter((place) => !!place && this.predictions.length !== 0), switchMap((place) => this._placeToAddress(place)), takeUntil(this._destroy$))
                .subscribe((address) => {
                this._selectAddress(address);
                this._clearPredictions();
            });
            fromEvent(this.searchElement.nativeElement, 'keyup')
                .pipe(tap(() => {
                this.stateChanges.next();
            }), debounceTime(200), filter((event) => {
                return event.code !== 'Enter' && event.code !== 'Tab';
            }), map((event) => {
                return event.target.value;
            }), tap((text) => {
                if (!!!text) {
                    this._clearPredictions();
                }
            }), filter((value) => !!value), tap((value) => {
                this._searchText = value;
                if (!value) {
                    this._address = {
                        ...this._address,
                        street: value,
                    };
                    this._selectAddress(this._address);
                }
            }), distinctUntilChanged(), switchMap((text) => {
                return this._getPlacePredictions(text);
            }), takeUntil(this._destroy$))
                .subscribe((response) => {
                this._ngZone.run(() => {
                    this.predictions = [
                        ...response.predictions,
                    ];
                    this._cdRef.markForCheck();
                });
            });
        });
    }
    _clearPredictions() {
        this.predictions = [];
        this._cdRef.markForCheck();
    }
    _selectAddress(address) {
        this.value = address;
        this.addressChange.emit(address);
    }
    _placeToAddress(place) {
        return of(place)
            .pipe(switchMap((place) => {
            if (!place || !this.googlePlacesService) {
                return of(null);
            }
            if (place && !place.place_id) {
                return of({
                    ...this.value,
                    street: place.name,
                    manual: true,
                });
            }
            return this._getPlaceDetails(place);
        }), map((response) => {
            let address;
            if (response.result) {
                address = googleDetailsToAddress(response.result, this.config);
                address.description = response.place.description;
            }
            else {
                address = response;
            }
            return address;
        }));
    }
    _listenAutocompleteSelection() {
        this.autoCompleteRef.optionSelected
            .pipe(map((option) => {
            return option.option.value;
        }), switchMap((place) => this._placeToAddress(place)), takeUntil(this._destroy$))
            .subscribe((address) => {
            this._ngZone.run(() => {
                this.searchElement.nativeElement.blur();
                this.value = address;
                const { unit } = extractUnit(this._searchText);
                if (unit) {
                    address.address2 = unit;
                }
                this.addressChange.emit(address);
                this.inputAddress = address;
                this._cdRef.markForCheck();
            });
        });
    }
    _initGoogleMap() {
        this._ngZone.runOutsideAngular(() => {
            this._map.loaded$
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => {
                this.googleAutocompleteService = new google.maps.places.AutocompleteService();
                this.googlePlacesService = new google.maps.places.PlacesService(this.searchElement.nativeElement);
            });
        });
    }
    _getPlacePredictions(address) {
        const { text } = extractUnit(address);
        const placesRequest = this.googleAutocompleteService
            .getPlacePredictions({ input: text }, () => { });
        return placesRequest
            .then((result) => {
            return {
                value: text,
                predictions: result?.predictions || [],
            };
        })
            .catch(() => {
            return {
                value: text,
                predictions: [],
            };
        });
    }
    _getPlaceDetails(place) {
        const getDetailsFactory = bindCallback(this.googlePlacesService.getDetails.bind(this.googlePlacesService));
        return getDetailsFactory(place)
            .pipe(map(([result, status]) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK) {
                return null;
            }
            else {
                return {
                    place,
                    result,
                };
            }
        }));
    }
    _registerFocusMonitor() {
        this._fm.monitor(this._elementRef, true)
            .pipe(filter(() => !this.disabled), takeUntil(this._destroy$))
            .subscribe((origin) => {
            this.focused = !!origin;
            this.stateChanges.next();
        });
    }
}
FsAddressAutocompleteComponent.nextId = 0;
FsAddressAutocompleteComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressAutocompleteComponent, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: i2.FsMap }, { token: i0.NgZone }, { token: i3.FocusMonitor }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsAddressAutocompleteComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressAutocompleteComponent, selector: "fs-address-autocomplete", inputs: { format: "format", readonly: "readonly", showClear: "showClear", suggestions: "suggestions", config: "config", disabled: "disabled", required: "required", placeholder: "placeholder" }, outputs: { addressChange: "addressChange" }, host: { properties: { "id": "this.id", "class.floating": "this.shouldLabelFloat" } }, providers: [
        {
            provide: MatFormFieldControl,
            useExisting: FsAddressAutocompleteComponent,
        },
    ], viewQueries: [{ propertyName: "searchElement", first: true, predicate: ["searchInput"], descendants: true, read: ElementRef, static: true }, { propertyName: "autoCompleteRef", first: true, predicate: MatAutocomplete, descendants: true, static: true }, { propertyName: "autocompleteTrigger", first: true, predicate: MatAutocompleteTrigger, descendants: true, static: true }], ngImport: i0, template: "<mat-form-field [floatLabel]=\"empty ? 'auto' : 'always'\">\n  <mat-label>{{placeholder}}</mat-label>\n  <ng-content></ng-content>\n  <input\n    matInput\n    type=\"search\"\n    autocomplete=\"off\"\n    [(ngModel)]=\"inputAddress\"\n    [matAutocomplete]=\"autocomplete\"\n    [name]=\"autocompleteName\"\n    [disabled]=\"disabled\"\n    [fsClear]=\"showClear && !empty && !disabled && !readonly\"\n    (cleared)=\"clear()\"\n    #searchInput>\n  <mat-autocomplete\n      [displayWith]=\"displayWith\"\n      (closed)=\"autocompletePanelClosed()\"\n      autoActiveFirstOption\n      [class]=\"'fs-autocomplete-pane'\"\n      #autocomplete=\"matAutocomplete\">\n    <mat-option *ngFor=\"let option of predictions\" [value]=\"option\">\n      {{ option.description }}\n    </mat-option>\n    <div *ngIf=\"!config.hideEnterManually\" class=\"static-options\">\n      <mat-option [value]=\"{ id: 'manual', name: searchInputValue }\">\n        Enter address manually\n      </mat-option>\n    </div>\n  </mat-autocomplete>\n  <mat-hint *ngIf=\"config.hint\">{{ config.hint }}</mat-hint>\n</mat-form-field>", styles: [".static-options{position:sticky;bottom:0;width:100%;background:#fff;border-top:1px solid #e0e0e0}.clear{cursor:pointer}mat-form-field{width:100%}\n"], components: [{ type: i4.MatFormField, selector: "mat-form-field", inputs: ["color", "appearance", "hideRequiredMarker", "hintLabel", "floatLabel"], exportAs: ["matFormField"] }, { type: i5.FsClearComponent, selector: "[fsClear]", inputs: ["ngModel", "fsClear"], outputs: ["ngModelChange", "cleared"] }, { type: i6.MatAutocomplete, selector: "mat-autocomplete", inputs: ["disableRipple"], exportAs: ["matAutocomplete"] }, { type: i7.MatOption, selector: "mat-option", exportAs: ["matOption"] }], directives: [{ type: i4.MatLabel, selector: "mat-label" }, { type: i8.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i6.MatAutocompleteTrigger, selector: "input[matAutocomplete], textarea[matAutocomplete]", exportAs: ["matAutocompleteTrigger"] }, { type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i9.FsFormNoFsValidatorsDirective, selector: "[ngModel]:not([required]):not([fsFormRequired]):not([fsFormCompare]):not([fsFormDateRange]):not([fsFormEmail]):not([fsFormEmails]):not([fsFormFunction]):not([fsFormGreater]):not([fsFormInteger]):not([fsFormLesser]):not([fsFormMax]):not([fsFormMaxLength]):not([fsFormMin]):not([fsFormMinLength]):not([fsFormNumeric]):not([fsFormPattern]):not([fsFormPhone]):not([fsFormUrl]):not([validate])" }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.MatHint, selector: "mat-hint", inputs: ["align", "id"] }], viewProviders: [
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [[new Optional(), NgForm]],
        }
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressAutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address-autocomplete', providers: [
                        {
                            provide: MatFormFieldControl,
                            useExisting: FsAddressAutocompleteComponent,
                        },
                    ], changeDetection: ChangeDetectionStrategy.OnPush, viewProviders: [
                        {
                            provide: ControlContainer,
                            useFactory: controlContainerFactory,
                            deps: [[new Optional(), NgForm]],
                        }
                    ], template: "<mat-form-field [floatLabel]=\"empty ? 'auto' : 'always'\">\n  <mat-label>{{placeholder}}</mat-label>\n  <ng-content></ng-content>\n  <input\n    matInput\n    type=\"search\"\n    autocomplete=\"off\"\n    [(ngModel)]=\"inputAddress\"\n    [matAutocomplete]=\"autocomplete\"\n    [name]=\"autocompleteName\"\n    [disabled]=\"disabled\"\n    [fsClear]=\"showClear && !empty && !disabled && !readonly\"\n    (cleared)=\"clear()\"\n    #searchInput>\n  <mat-autocomplete\n      [displayWith]=\"displayWith\"\n      (closed)=\"autocompletePanelClosed()\"\n      autoActiveFirstOption\n      [class]=\"'fs-autocomplete-pane'\"\n      #autocomplete=\"matAutocomplete\">\n    <mat-option *ngFor=\"let option of predictions\" [value]=\"option\">\n      {{ option.description }}\n    </mat-option>\n    <div *ngIf=\"!config.hideEnterManually\" class=\"static-options\">\n      <mat-option [value]=\"{ id: 'manual', name: searchInputValue }\">\n        Enter address manually\n      </mat-option>\n    </div>\n  </mat-autocomplete>\n  <mat-hint *ngIf=\"config.hint\">{{ config.hint }}</mat-hint>\n</mat-form-field>", styles: [".static-options{position:sticky;bottom:0;width:100%;background:#fff;border-top:1px solid #e0e0e0}.clear{cursor:pointer}mat-form-field{width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i2.FsMap }, { type: i0.NgZone }, { type: i3.FocusMonitor }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { format: [{
                type: Input
            }], readonly: [{
                type: Input
            }], showClear: [{
                type: Input
            }], suggestions: [{
                type: Input
            }], config: [{
                type: Input
            }], addressChange: [{
                type: Output
            }], searchElement: [{
                type: ViewChild,
                args: ['searchInput', { static: true, read: ElementRef }]
            }], autoCompleteRef: [{
                type: ViewChild,
                args: [MatAutocomplete, { static: true }]
            }], autocompleteTrigger: [{
                type: ViewChild,
                args: [MatAutocompleteTrigger, { static: true }]
            }], id: [{
                type: HostBinding
            }], disabled: [{
                type: Input
            }], required: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], shouldLabelFloat: [{
                type: HostBinding,
                args: ['class.floating']
            }] } });

const Countries = [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AO', name: 'Angola' },
    { code: 'AI', name: 'Anguilla' },
    { code: 'AQ', name: 'Antarctica' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AW', name: 'Aruba' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BM', name: 'Bermuda' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BV', name: 'Bouvet Island' },
    { code: 'BR', name: 'Brazil' },
    { code: 'IO', name: 'British Indian Ocean Territory' },
    { code: 'BN', name: 'Brunei Darussalam' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' },
    {
        code: 'CA', name: 'Canada',
        regionLabel: 'Province',
        regions: [
            { code: 'AB', name: 'Alberta' },
            { code: 'BC', name: 'British Columbia' },
            { code: 'MB', name: 'Manitoba' },
            { code: 'NB', name: 'New Brunswick' },
            { code: 'NL', name: 'Newfoundland and Labrador' },
            { code: 'NT', name: 'Northwest Territories' },
            { code: 'NS', name: 'Nova Scotia' },
            { code: 'NU', name: 'Nunavut' },
            { code: 'ON', name: 'Ontario' },
            { code: 'PE', name: 'Prince Edward Island' },
            { code: 'QC', name: 'Quebec' },
            { code: 'SK', name: 'Saskatchewan' },
            { code: 'YT', name: 'Yukon Territory' },
        ],
    },
    { code: 'CV', name: 'Cape Verde' },
    { code: 'KY', name: 'Cayman Islands' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CX', name: 'Christmas Island' },
    { code: 'CC', name: 'Cocos (Keeling) Islands' },
    { code: 'CO', name: 'Colombia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CG', name: 'Congo' },
    { code: 'CD', name: 'Congo, the Democratic Republic of the' },
    { code: 'CK', name: 'Cook Islands' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'CI', name: 'Cote D\'Ivoire' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'EE', name: 'Estonia' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FK', name: 'Falkland Islands (Malvinas)' },
    { code: 'FO', name: 'Faroe Islands' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GF', name: 'French Guiana' },
    { code: 'PF', name: 'French Polynesia' },
    { code: 'TF', name: 'French Southern Territories' },
    { code: 'GA', name: 'Gabon' },
    { code: 'GM', name: 'Gambia' },
    { code: 'GE', name: 'Georgia' },
    { code: 'DE', name: 'Germany' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GI', name: 'Gibraltar' },
    { code: 'GR', name: 'Greece' },
    { code: 'GL', name: 'Greenland' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GP', name: 'Guadeloupe' },
    { code: 'GU', name: 'Guam' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HM', name: 'Heard Island and Mcdonald Islands' },
    { code: 'VA', name: 'Holy See (Vatican City State)' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran, Islamic Republic of' },
    { code: 'IQ', name: 'Iraq' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'JP', name: 'Japan' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'KI', name: 'Kiribati' },
    { code: 'KP', name: 'Korea, Democratic People\'s Republic of' },
    { code: 'KR', name: 'Korea, Republic of' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Lao People\'s Democratic Republic' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'LR', name: 'Liberia' },
    { code: 'LY', name: 'Libyan Arab Jamahiriya' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MO', name: 'Macao' },
    { code: 'MK', name: 'Macedonia' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MW', name: 'Malawi' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' },
    { code: 'ML', name: 'Mali' },
    { code: 'MT', name: 'Malta' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MQ', name: 'Martinique' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'YT', name: 'Mayotte' },
    { code: 'MX', name: 'Mexico' },
    { code: 'FM', name: 'Micronesia, Federated States of' },
    { code: 'MD', name: 'Moldova, Republic of' },
    { code: 'MC', name: 'Monaco' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'MS', name: 'Montserrat' },
    { code: 'MA', name: 'Morocco' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'MM', name: 'Myanmar' },
    { code: 'NA', name: 'Namibia' },
    { code: 'NR', name: 'Nauru' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'AN', name: 'Netherlands Antilles' },
    { code: 'NC', name: 'New Caledonia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'NE', name: 'Niger' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'NU', name: 'Niue' },
    { code: 'NF', name: 'Norfolk Island' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'NO', name: 'Norway' },
    { code: 'OM', name: 'Oman' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PW', name: 'Palau' },
    { code: 'PS', name: 'Palestinian Territory, Occupied' },
    { code: 'PA', name: 'Panama' },
    { code: 'PG', name: 'Papua New Guinea' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PN', name: 'Pitcairn' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'QA', name: 'Qatar' },
    { code: 'RE', name: 'Reunion' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russian Federation' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'SH', name: 'Saint Helena' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'PM', name: 'Saint Pierre and Miquelon' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'WS', name: 'Samoa' },
    { code: 'SM', name: 'San Marino' },
    { code: 'ST', name: 'Sao Tome and Principe' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SN', name: 'Senegal' },
    { code: 'CS', name: 'Serbia and Montenegro' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SB', name: 'Solomon Islands' },
    { code: 'SO', name: 'Somalia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'GS', name: 'South Georgia and Sandwich Isles' },
    { code: 'ES', name: 'Spain' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'SD', name: 'Sudan' },
    { code: 'SR', name: 'Suriname' },
    { code: 'SJ', name: 'Svalbard and Jan Mayen' },
    { code: 'SZ', name: 'Swaziland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SY', name: 'Syrian Arab Republic' },
    { code: 'TW', name: 'Taiwan (ROC)' },
    { code: 'TJ', name: 'Tajikistan' },
    { code: 'TZ', name: 'Tanzania, United Republic of' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'TG', name: 'Togo' },
    { code: 'TK', name: 'Tokelau' },
    { code: 'TO', name: 'Tonga' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'TM', name: 'Turkmenistan' },
    { code: 'TC', name: 'Turks and Caicos Islands' },
    { code: 'TV', name: 'Tuvalu' },
    { code: 'UG', name: 'Uganda' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    {
        code: 'US',
        name: 'United States',
        regionLabel: 'State',
        regions: [
            { code: 'AK', name: 'Alaska' },
            { code: 'AL', name: 'Alabama' },
            { code: 'AR', name: 'Arkansas' },
            { code: 'AS', name: 'American Samoa' },
            { code: 'AZ', name: 'Arizona' },
            { code: 'CA', name: 'California' },
            { code: 'CO', name: 'Colorado' },
            { code: 'CT', name: 'Connecticut' },
            { code: 'DE', name: 'Delaware' },
            { code: 'FL', name: 'Florida' },
            { code: 'GA', name: 'Georgia' },
            { code: 'HI', name: 'Hawaii' },
            { code: 'IA', name: 'Iowa' },
            { code: 'ID', name: 'Idaho' },
            { code: 'IL', name: 'Illinois' },
            { code: 'IN', name: 'Indiana' },
            { code: 'KS', name: 'Kansas' },
            { code: 'KY', name: 'Kentucky' },
            { code: 'LA', name: 'Louisiana' },
            { code: 'MA', name: 'Massachusetts' },
            { code: 'MD', name: 'Maryland' },
            { code: 'ME', name: 'Maine' },
            { code: 'MI', name: 'Michigan' },
            { code: 'MN', name: 'Minnesota' },
            { code: 'MO', name: 'Missouri' },
            { code: 'MP', name: 'Northern Mariana Islands' },
            { code: 'MS', name: 'Mississippi' },
            { code: 'MT', name: 'Montana' },
            { code: 'NC', name: 'North Carolina' },
            { code: 'ND', name: 'North Dakota' },
            { code: 'NE', name: 'Nebraska' },
            { code: 'NH', name: 'New Hampshire' },
            { code: 'NJ', name: 'New Jersey' },
            { code: 'NM', name: 'New Mexico' },
            { code: 'NV', name: 'Nevada' },
            { code: 'NY', name: 'New York' },
            { code: 'OH', name: 'Ohio' },
            { code: 'OK', name: 'Oklahoma' },
            { code: 'OR', name: 'Oregon' },
            { code: 'PA', name: 'Pennsylvania' },
            { code: 'PR', name: 'Puerto Rico' },
            { code: 'RI', name: 'Rhode Island' },
            { code: 'SC', name: 'South Carolina' },
            { code: 'SD', name: 'South Dakota' },
            { code: 'TN', name: 'Tennessee' },
            { code: 'TX', name: 'Texas' },
            { code: 'UT', name: 'Utah' },
            { code: 'VA', name: 'Virginia' },
            { code: 'VI', name: 'Virgin Islands' },
            { code: 'VT', name: 'Vermont' },
            { code: 'WA', name: 'Washington' },
            { code: 'WI', name: 'Wisconsin' },
            { code: 'WV', name: 'West Virginia' },
            { code: 'WY', name: 'Wyoming' },
        ],
    },
    { code: 'UM', name: 'United States Minor Outlying Islands' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VU', name: 'Vanuatu' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Viet Nam' },
    { code: 'VG', name: 'Virgin Islands, British' },
    { code: 'VI', name: 'Virgin Islands, U.s.' },
    { code: 'WF', name: 'Wallis and Futuna' },
    { code: 'EH', name: 'Western Sahara' },
    { code: 'YE', name: 'Yemen' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'ZW', name: 'Zimbabwe' },
];

var Country;
(function (Country) {
    Country["Canada"] = "CA";
    Country["UnitedStates"] = "US";
})(Country || (Country = {}));

class FsAddressRegionComponent {
    constructor(_cdRef) {
        this._cdRef = _cdRef;
        this.disabled = false;
        this.required = false;
        this.regionCountryOrder = [Country.Canada, Country.UnitedStates];
        this.regionChange = new EventEmitter();
        this.controlName = `region${guid('xxxxxx')}`;
        this.countryEnum = Country;
        this._countries = [];
        this._destroy$ = new Subject();
        this.fetch = (keyword) => {
            keyword = keyword.toLowerCase();
            return of(null)
                .pipe(map(() => {
                const regions = this._countries
                    .reduce((accum, country) => {
                    const countryRegions = (country.regions || [])
                        .filter((region) => {
                        const regionName = region.name.toLowerCase().trim();
                        return regionName.indexOf(keyword) !== -1;
                    });
                    return [
                        ...accum,
                        ...countryRegions
                            .map((countryRegion) => {
                            return {
                                ...countryRegion,
                                country: country.name,
                            };
                        }),
                    ];
                }, []);
                return regions;
            }));
        };
        this.displayWith = (data) => {
            return data?.name;
        };
        this.justUseShow = (keyword) => {
            return !!keyword;
        };
        this.countries = [Country.Canada, Country.UnitedStates];
    }
    set region(regionCode) {
        const region = this.addressCountries
            .reduce((accum, addressCountry) => {
            return [
                ...accum,
                ...(addressCountry.regions || [])
                    .filter((addressRegion) => (addressRegion.code === regionCode &&
                    (!this.country || this.country === addressCountry.code))),
            ];
        }, [])[0];
        this.regionModel = regionCode ? (region ? region : {
            name: regionCode,
        }) : null;
    }
    get region() {
        return this.regionModel?.code;
    }
    set countries(countryCodes) {
        countryCodes = countryCodes || [Country.Canada, Country.UnitedStates];
        this._countries = countryCodes
            .map((countryCode) => {
            return Countries.find((country) => country.code === countryCode);
        });
        this.updateCountryRegionLabels();
    }
    get addressCountries() {
        return this._countries;
    }
    ngOnInit() {
        this.updateCountryRegionLabels();
        this._listenControlStateChanges();
    }
    clear() {
        this.regionModel = null;
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    selectUserOption(keyword) {
        this.regionModel = {
            code: keyword,
            name: keyword,
        };
        this.regionChange.emit(keyword);
    }
    regionChanged() {
        this.regionChange.emit(this.regionModel?.code);
    }
    updateCountryRegionLabels() {
        this.regionLabel = this.label ? this.label : Object.keys(this._countries
            .reduce((accum, country) => {
            return {
                ...accum,
                [country.regionLabel || 'Province']: true,
            };
        }, {}))
            .join('/');
    }
    // we need this to get updated ng-(invalid/dirty) classes
    _listenControlStateChanges() {
        this.autocompleteModel
            .control
            .statusChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._cdRef.markForCheck();
        });
    }
}
FsAddressRegionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsAddressRegionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressRegionComponent, selector: "fs-address-region", inputs: { region: "region", disabled: "disabled", country: "country", label: "label", required: "required", regionCountryOrder: "regionCountryOrder", countries: "countries" }, outputs: { regionChange: "regionChange" }, viewQueries: [{ propertyName: "autocompleteModel", first: true, predicate: FsAutocompleteComponent, descendants: true, read: NgModel, static: true }], ngImport: i0, template: "<fs-autocomplete\n    [fetch]=\"fetch\"\n    [displayWith]=\"displayWith\"\n    [fetchOnFocus]=\"true\"\n    [(ngModel)]=\"regionModel\"\n    (ngModelChange)=\"regionChanged()\"\n    [placeholder]=\"regionLabel\"\n    [disabled]=\"disabled\"\n    [fsFormRequired]=\"required\"\n    [name]=\"controlName\">\n  <ng-template fsAutocompleteTemplate let-data=\"data\">\n    <span class=\"country-region\">\n      <span>{{ data.name }}</span>\n      <ng-container *ngIf=\"!regionModel && addressCountries.length > 1\">\n        <span>{{data.country}}</span>\n      </ng-container>\n    </span>\n  </ng-template>\n  <ng-template\n      fsAutocompleteStatic\n      let-keyword\n      (selected)=\"selectUserOption($event)\"\n      [show]=\"justUseShow\">\n    Just Use \"{{ keyword }}\"\n  </ng-template>\n\n  <ng-template fsAutocompleteNoResults></ng-template>\n\n</fs-autocomplete>\n", styles: ["@charset \"UTF-8\";.country-region{display:inline-flex}.country-region span:not(:last-child):after{content:\",\\a0\"}\n"], components: [{ type: i1$2.FsAutocompleteComponent, selector: "fs-autocomplete", inputs: ["fetch", "placeholder", "displayWith", "fetchOnFocus", "readonly", "required", "disabled", "appearance", "hint", "panelWidth", "panelClass", "showClear"], outputs: ["cleared", "opened", "closed"] }], directives: [{ type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i9.FsFormRequiredDirective, selector: "[fsFormRequired],[ngModel][required]", inputs: ["fsFormRequired", "required", "fsFormRequiredMessage"] }, { type: i1$2.FsAutocompleteTemplateDirective, selector: "[fsAutocompleteTemplate]" }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$2.FsAutocompleteStaticDirective, selector: "[fsAutocompleteStatic],[fsAutocompleteStaticTemplate]", inputs: ["show", "disable"], outputs: ["selected"] }, { type: i1$2.FsAutocompleteNoResultsDirective, selector: "[fsAutocompleteNoResults]" }], viewProviders: [
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [[new Optional(), NgForm]],
        },
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address-region', changeDetection: ChangeDetectionStrategy.OnPush, viewProviders: [
                        {
                            provide: ControlContainer,
                            useFactory: controlContainerFactory,
                            deps: [[new Optional(), NgForm]],
                        },
                    ], template: "<fs-autocomplete\n    [fetch]=\"fetch\"\n    [displayWith]=\"displayWith\"\n    [fetchOnFocus]=\"true\"\n    [(ngModel)]=\"regionModel\"\n    (ngModelChange)=\"regionChanged()\"\n    [placeholder]=\"regionLabel\"\n    [disabled]=\"disabled\"\n    [fsFormRequired]=\"required\"\n    [name]=\"controlName\">\n  <ng-template fsAutocompleteTemplate let-data=\"data\">\n    <span class=\"country-region\">\n      <span>{{ data.name }}</span>\n      <ng-container *ngIf=\"!regionModel && addressCountries.length > 1\">\n        <span>{{data.country}}</span>\n      </ng-container>\n    </span>\n  </ng-template>\n  <ng-template\n      fsAutocompleteStatic\n      let-keyword\n      (selected)=\"selectUserOption($event)\"\n      [show]=\"justUseShow\">\n    Just Use \"{{ keyword }}\"\n  </ng-template>\n\n  <ng-template fsAutocompleteNoResults></ng-template>\n\n</fs-autocomplete>\n", styles: ["@charset \"UTF-8\";.country-region{display:inline-flex}.country-region span:not(:last-child):after{content:\",\\a0\"}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { autocompleteModel: [{
                type: ViewChild,
                args: [FsAutocompleteComponent, { read: NgModel, static: true }]
            }], region: [{
                type: Input
            }], disabled: [{
                type: Input
            }], country: [{
                type: Input
            }], label: [{
                type: Input
            }], required: [{
                type: Input
            }], regionCountryOrder: [{
                type: Input
            }], countries: [{
                type: Input
            }], regionChange: [{
                type: Output
            }] } });

function addressFormat(address, options = {}) {
    options = {
        format: AddressFormat.OneLine,
        ...options,
    };
    const parts = ['name', 'street', 'address2', 'address3', 'city', 'region', 'zip', 'country'];
    let addressParts = [];
    let lines = [];
    if (address) {
        parts.forEach((part) => {
            if (address[part]) {
                addressParts.push(address[part]);
            }
        });
    }
    if (options.includeFirst) {
        addressParts = addressParts.slice(0, options.includeFirst);
    }
    if (addressParts.length) {
        if (options.format === AddressFormat.TwoLine) {
            lines = [[addressParts.shift()]];
            lines.push(addressParts);
        }
        else {
            lines = addressParts;
        }
    }
    return lines;
}

function addressOneLineFormat(address, options = {}) {
    const addressOptions = {
        format: AddressFormat.OneLine,
        ...options,
    };
    return addressFormat(address, addressOptions);
}

function addressTwoLineFormat(address, options = {}) {
    const addressOptions = {
        format: AddressFormat.TwoLine,
        ...options,
    };
    return addressFormat(address, addressOptions);
}

function addressSummaryFormat(address) {
    const parts = ['name', 'street', 'address2', 'address3', 'city', 'region', 'country'];
    const addressParts = [];
    if (address) {
        for (let i = 0; i < parts.length; i++) {
            const field = parts[i];
            const part = address[field];
            if (field === 'name' && part) {
                addressParts.push(part);
                break;
            }
            else if (part && field !== 'name') {
                addressParts.push(part);
                const nextPart = address[parts[i + 1]];
                if (nextPart) {
                    addressParts.push(nextPart);
                }
                break;
            }
        }
    }
    return addressParts;
}

function searchCountryRegions(text, regions, limit) {
    let matches = [];
    text = text.toLowerCase().trim();
    regions.forEach((region) => {
        const regionName = region.name.toLowerCase().trim();
        const index = regionName.indexOf(text);
        if (index > -1) {
            matches.push({
                index,
                region,
            });
        }
    });
    matches.sort((a, b) => {
        if (a.index < b.index) {
            return -1;
        }
        else if (a.index > b.index) {
            return 1;
        }
        return 0;
    });
    if (limit) {
        matches = matches.slice(0, limit);
    }
    return matches.map((match) => match.region);
}

class FsAddressCountryComponent {
    constructor(_cdRef) {
        this._cdRef = _cdRef;
        this.disabled = false;
        this.required = false;
        this.countries = Countries;
        this.selectionChange = new EventEmitter();
        this.onChange = (data) => { };
        this.onTouched = () => { };
        this._placeholder = null;
        this.fetch = (keyword) => {
            return of(keyword)
                .pipe(map((kw) => {
                return searchCountryRegions(kw, this.countries, 10);
            }));
        };
        this.displayWith = (data) => {
            return data.name;
        };
    }
    set placeholder(value) {
        this._placeholder = value || 'Country';
    }
    get placeholder() {
        return this._placeholder;
    }
    writeValue(data) {
        if (data) {
            this.country = this.countries.find((country) => country.code === data);
            this._cdRef.markForCheck();
        }
        else {
            this.country = data;
        }
    }
    changed(value) {
        const code = value?.code;
        this.onChange(code);
        this.selectionChange.emit(code);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    ngOnChanges(changes) {
        if (changes.excludeCountries && changes.excludeCountries.currentValue) {
            this.countries = this.countries.filter((country) => {
                return this.excludeCountries.indexOf(country.code) === -1;
            });
        }
    }
}
FsAddressCountryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountryComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsAddressCountryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressCountryComponent, selector: "fs-address-country", inputs: { disabled: "disabled", required: "required", excludeCountries: "excludeCountries", countries: "countries", placeholder: "placeholder" }, outputs: { selectionChange: "selectionChange" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FsAddressCountryComponent),
        }], usesOnChanges: true, ngImport: i0, template: "<fs-autocomplete\n    [fetch]=\"fetch\"\n    [displayWith]=\"displayWith\"\n    [fetchOnFocus]=\"true\"\n    [(ngModel)]=\"country\"\n    (ngModelChange)=\"changed($event)\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [fsFormRequired]=\"required\"\n    name=\"country\">\n  <ng-template fsAutocompleteTemplate let-data=\"data\">\n    {{ data.name }}\n  </ng-template>\n</fs-autocomplete>\n", styles: [""], components: [{ type: i1$2.FsAutocompleteComponent, selector: "fs-autocomplete", inputs: ["fetch", "placeholder", "displayWith", "fetchOnFocus", "readonly", "required", "disabled", "appearance", "hint", "panelWidth", "panelClass", "showClear"], outputs: ["cleared", "opened", "closed"] }], directives: [{ type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i9.FsFormRequiredDirective, selector: "[fsFormRequired],[ngModel][required]", inputs: ["fsFormRequired", "required", "fsFormRequiredMessage"] }, { type: i1$2.FsAutocompleteTemplateDirective, selector: "[fsAutocompleteTemplate]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address-country', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => FsAddressCountryComponent),
                        }], changeDetection: ChangeDetectionStrategy.OnPush, template: "<fs-autocomplete\n    [fetch]=\"fetch\"\n    [displayWith]=\"displayWith\"\n    [fetchOnFocus]=\"true\"\n    [(ngModel)]=\"country\"\n    (ngModelChange)=\"changed($event)\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [fsFormRequired]=\"required\"\n    name=\"country\">\n  <ng-template fsAutocompleteTemplate let-data=\"data\">\n    {{ data.name }}\n  </ng-template>\n</fs-autocomplete>\n", styles: [""] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { disabled: [{
                type: Input
            }], required: [{
                type: Input
            }], excludeCountries: [{
                type: Input
            }], countries: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], selectionChange: [{
                type: Output
            }] } });

class FsAddressComponent {
    constructor() {
        this.regionCountryOrder = [Country.Canada, Country.UnitedStates];
        this.suggestions = false;
        this.addressChange = new EventEmitter();
        this.collapseChange = new EventEmitter();
        this.controlNames = {
            street: `street_${guid('xxxxxxxx')}`,
            locationName: `location_name_${guid('xxxxxxxx')}`,
            address2: `address2_${guid('xxxxxxxx')}`,
            address3: `address3_${guid('xxxxxxxx')}`,
            city: `city_${guid('xxxxxxxx')}`,
            addressCountry: `address_country_${guid('xxxxxxxx')}`,
            zip: `zip_${guid('xxxxxxxx')}`,
        };
        this.config = {};
        this.countries = Countries;
        this.isSearched = false;
        this._destory$ = new Subject();
    }
    set setConfig(config) {
        config.search = config.search === undefined ? false : config.search;
        if (!isObject(config.map)) {
            config.map = { showMap: false };
        }
        this.config = config;
    }
    ngOnInit() {
        this._initAddress();
        this._initConfig();
        this._initMap();
        this._initCountries();
        this._initZipAndStateLabels();
        this._initCollapseBtn();
        // // Example ready event. Allow to use google object and map instance
        // if (this.agmMap) {
        //   this.agmMap
        //     .mapReady
        //     .pipe(
        //       takeUntil(this._destory$)
        //     )
        //     .subscribe(() => {
        //       this.agmMap.triggerResize();
        //       if (this.address.name ||
        //         this.address.country ||
        //         this.address.region ||
        //         this.address.city ||
        //         this.address.zip) {
        //           this.address.lat = 9999;
        //           this.address.lng = 9999;
        //           this.change();
        //       }
        //     });
        //   }
    }
    get regionCountries() {
        return this.countries.map((country) => country.code);
    }
    ngOnChanges(change) {
        if (change.address) {
            if (!change.address.currentValue) {
                this.address = {};
            }
            if (!change.address.firstChange) {
                const currentCountry = change.address.currentValue ? change.address.currentValue.country : null;
                const previousCountry = change.address.previousValue ? change.address.previousValue.country : null;
                if (currentCountry !== previousCountry) {
                    this._initZipAndStateLabels();
                }
            }
        }
    }
    ngOnDestroy() {
        this._destory$.next();
        this._destory$.complete();
    }
    recenter() {
        this.mapConfig.center = { latitude: this.address.lat, longitude: this.address.lng };
        this.mapConfig.marker.coords.latitude = this.address.lat;
        this.mapConfig.marker.coords.longitude = this.address.lng;
        this.map.setCenter(this.address.lat, this.address.lng);
    }
    dragEnded(event) {
        this.mapConfig.marker.events.dragend(event);
    }
    changeCountry() {
        const country = this.countries.find((item) => item.code === this.address.country);
        if (country && country.regions) {
            const region = country.regions.some((item) => item.code === this.address.region);
            if (!region) {
                this.address.region = null;
            }
        }
        else {
            this.address.region = null;
        }
        this.fsAddressRegionComponent.region = this.address.region;
        this._initZipAndStateLabels();
        this.change();
    }
    changeRegion() {
        if (!!this.address.region) {
            const regionCountry = this.countries.find((country) => {
                return country.regions
                    && country.regions.find((region) => this.address.region === region.code);
            });
            if (regionCountry) {
                this.address.country = regionCountry.code;
            }
        }
        this.change();
    }
    change(event) {
        if (event) {
            event.stopPropagation();
        }
        if (!this.config.search) {
            return this.addressChange.emit(this.address);
        }
        const geocoder = new google.maps.Geocoder();
        const parts = [
            this.address.country,
            this.address.region,
            this.address.city,
            this.address.zip,
            this.address.street,
            this.address.name,
        ];
        this.searchedAddress = parts.filter((part) => part).join(', ');
        this.addressChange.emit(this.address);
        geocoder.geocode({ address: this.searchedAddress }, (results, status) => {
            this.isSearched = true;
            const newAddress = { ...this.address };
            if (status === google.maps.GeocoderStatus.OK && results.length > 0) {
                const location = results[0].geometry.location;
                newAddress.description = results[0].formatted_address;
                newAddress.lat = location.lat();
                newAddress.lng = location.lng();
                this.mapConfig.center = { latitude: location.lat(), longitude: location.lng() };
                this.mapConfig.marker.coords.latitude = location.lat();
                this.mapConfig.marker.coords.longitude = location.lng();
            }
            else {
                newAddress.lat = null;
                newAddress.lng = null;
            }
            this.addressChange.emit(newAddress);
        });
    }
    _initAddress() {
        this.address = {
            name: null,
            country: null,
            region: null,
            address2: null,
            address3: null,
            street: null,
            city: null,
            zip: null,
            lat: null,
            lng: null, ...this.address,
        };
    }
    _initConfig() {
        this.config = {
            name: { required: false, visible: true },
            country: { required: false, visible: true },
            region: { required: false, visible: true },
            address2: { required: false, visible: false },
            address3: { required: false, visible: false },
            city: { required: false, visible: true },
            street: { required: false, visible: true },
            zip: { required: false, visible: true }, ...this.config,
        };
    }
    _initMap() {
        this.mapConfig = {
            center: {
                latitude: this.address.lat || 9999,
                longitude: this.address.lng || 9999,
            },
            zoom: 13,
            scrollwheel: false,
            streetViewControl: false,
            zoomControl: true,
            mapTypeControlOptions: { mapTypeIds: [] },
            marker: {
                id: 0,
                coords: { latitude: this.address.lat, longitude: this.address.lng },
                options: { draggable: true },
                events: {
                    dragend: (marker) => {
                        this.address.lat = marker.coords.lat;
                        this.address.lng = marker.coords.lng;
                        this.addressChange.emit(this.address);
                    },
                },
            }, ...this.config.map,
        };
    }
    _initCountries() {
        if (this.config.country && this.config.country.list && this.config.country.list.length) {
            this.countries.length = 0;
            this.config.country.list.forEach((el) => {
                const country = this.countries.find((countryEl) => countryEl.code === el);
                if (country) {
                    this.countries.push(country);
                }
            });
        }
        let isEmpty = true;
        Object.keys(this.address).forEach((objectKey) => {
            if (this.address[objectKey]) {
                isEmpty = false;
                return;
            }
        });
    }
    _initZipAndStateLabels() {
        this._updateCountryRegionLabels();
    }
    _updateCountryRegionLabels() {
        if (this.address.country) {
            this.zipLabel = this.address.country === Country.UnitedStates
                ? 'ZIP Code'
                : 'Postal Code';
        }
        else {
            this.zipLabel = 'ZIP/Postal Code';
        }
        this.zipLabel = this.config.zip.placeholder
            ? this.config.zip.placeholder
            : this.zipLabel;
    }
    _initCollapseBtn() {
        this.config.collapseButton = {
            show: true,
            title: 'Collapse Address Editor',
            color: 'primary',
            theme: 'mat-raised-button', ...this.config.collapseButton,
        };
    }
}
FsAddressComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsAddressComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressComponent, selector: "fs-address", inputs: { address: "address", excludeCountries: "excludeCountries", regionCountryOrder: "regionCountryOrder", suggestions: "suggestions", setConfig: ["config", "setConfig"] }, outputs: { addressChange: "addressChange", collapseChange: "collapseChange" }, viewQueries: [{ propertyName: "fsAddressRegionComponent", first: true, predicate: FsAddressRegionComponent, descendants: true }, { propertyName: "map", first: true, predicate: FsMapComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<mat-form-field\n    *ngIf=\"config.name.visible!==false\"\n    class=\"name\">\n  <mat-label>Location name</mat-label>\n  <input\n    matInput\n    autocomplete=\"nope\"\n    [(ngModel)]=\"address.name\"\n    [disabled]=\"config.name.disabled\"\n    [fsFormRequired]=\"config.name.required\"\n    [name]=\"controlNames.locationName\"    \n    (change)=\"change($event)\"\n    autocomplete=\"off\">\n</mat-form-field>\n\n<ng-container *ngIf=\"config.street.visible!==false\">\n  <ng-container *ngIf=\"suggestions; else textStreetField\">\n    <fs-address-autocomplete\n      [config]=\"config\"\n      [(ngModel)]=\"address\"\n      (ngModelChange)=\"change()\"\n      [disabled]=\"config.street.disabled\"\n      [suggestions]=\"suggestions\"\n      [showClear]=\"false\"\n      [placeholder]=\"config.street.placeholder || 'Street'\"\n      [name]=\"controlNames.street\">\n    </fs-address-autocomplete>\n  </ng-container>\n\n  <ng-template #textStreetField>\n    <mat-form-field class=\"street\">\n      <mat-label>{{config.street.placeholder || 'Street'}}</mat-label>\n      <input\n        matInput\n        type=\"search\"\n        autocomplete=\"nope\"\n        [(ngModel)]=\"address.street\"\n        [disabled]=\"config.street.disabled\"\n        [fsFormRequired]=\"config.street.required\"\n        [name]=\"controlNames.street\"\n        (change)=\"change()\"\n        autocomplete=\"off\">\n      </mat-form-field>\n  </ng-template>\n</ng-container>\n\n<mat-form-field\n    *ngIf=\"config.address2.visible\"\n    class=\"address2\">\n  <mat-label>{{config.address2.placeholder || 'Address 2'}}</mat-label>\n  <input\n    matInput\n    type=\"search\"\n    autocomplete=\"nope\"\n    [(ngModel)]=\"address.address2\"\n    [disabled]=\"config.address2.disabled\"\n    [fsFormRequired]=\"config.address2.required\"\n    [name]=\"controlNames.address2\"\n    (change)=\"change($event)\"\n    autocomplete=\"off\">\n</mat-form-field>\n\n<mat-form-field\n    *ngIf=\"config.address3.visible\"\n    class=\"address3\">\n  <mat-label>{{config.address3.placeholder || 'Address 3'}}</mat-label>\n  <input\n    matInput\n    type=\"search\"\n    autocomplete=\"nope\"\n    [(ngModel)]=\"address.address3\"\n    [disabled]=\"config.address3.disabled\"\n    [fsFormRequired]=\"config.address3.required\"\n    [name]=\"controlNames.address3\"\n    (change)=\"change($event)\"\n    autocomplete=\"off\">\n</mat-form-field>\n<div class=\"city-region-zip-country\" fxLayout.lt-sm=\"column\">\n  <div class=\"city\">\n    <mat-form-field *ngIf=\"config.city.visible!==false\">\n      <mat-label>{{config.city.placeholder || 'City'}}</mat-label>\n      <input\n        matInput\n        type=\"search\"\n        autocomplete=\"nope\"\n        [(ngModel)]=\"address.city\"\n        [disabled]=\"config.city.disabled\"\n        [fsFormRequired]=\"config.city.required\"\n        [name]=\"controlNames.city\"\n        (change)=\"change($event)\"\n        autocomplete=\"off\">\n    </mat-form-field>\n  </div>\n\n  <div class=\"region\">\n    <fs-address-region\n      *ngIf=\"config.region.visible!==false\"\n      [(region)]=\"address.region\"\n      (regionChange)=\"changeRegion()\"\n      [country]=\"address.country\"\n      [countries]=\"regionCountries\"\n      [label]=\"config.region.placeholder\"\n      [disabled]=\"config.region.disabled\"\n      [required]=\"config.region.required\"\n      [regionCountryOrder]=\"regionCountryOrder\">\n    </fs-address-region>\n  </div>\n\n  <div class=\"country\">\n    <fs-address-country\n      *ngIf=\"config.country.visible!==false\"\n      [disabled]=\"config.country.disabled\"\n      [required]=\"config.country.required\"\n      [placeholder]=\"config.country.placeholder\"\n      [excludeCountries]=\"excludeCountries\"\n      [(ngModel)]=\"address.country\"\n      (selectionChange)=\"changeCountry()\"\n      [name]=\"controlNames.addressCountry\">\n    </fs-address-country>\n  </div>\n\n  <div class=\"zip\">\n    <mat-form-field\n      *ngIf=\"config.zip.visible!==false\">\n      <mat-label>{{ zipLabel }}</mat-label>\n      <input\n        matInput\n        type=\"search\"\n        autocomplete=\"nope\"\n        [(ngModel)]=\"address.zip\"\n        [disabled]=\"config.zip.disabled\"\n        [fsFormRequired]=\"config.zip.required\"\n        [name]=\"controlNames.zip\"\n        (change)=\"change($event)\"\n        autocomplete=\"off\">\n    </mat-form-field>\n  </div>\n\n</div>\n\n<div class=\"map-container\" *ngIf=\"mapConfig.showMap\">\n  <fs-map\n      [height]=\"'300px'\"\n      [width]=\"'100%'\"\n      [lat]=\"mapConfig.center.latitude\"\n      [lng]=\"mapConfig.center.longitude\"\n      [zoom]=\"mapConfig.zoom\"\n      [scrollwheel]=\"mapConfig.scrollwheel\"\n      [streetViewControl]=\"mapConfig.streetViewControl\"\n      [zoomControl]=\"mapConfig.zoomControl\"\n      [mapTypeControlOptions]=\"mapConfig.mapTypeControlOptions\">\n    <fs-map-marker\n      [lat]=\"mapConfig.marker.coords.latitude\"\n      [lng]=\"mapConfig.marker.coords.longitude\">\n    </fs-map-marker>\n  </fs-map>\n\n  <div class=\"address-incomplete\" *ngIf=\"!address.lat && !address.lng\">\n    <div *ngIf=\"!isSearched\">Please populate the address above to locate it on the map</div>\n    <div *ngIf=\"isSearched\">Could not find address \"{{ searchedAddress }}\"</div>\n  </div>\n</div>\n", styles: ["agm-map{width:100%;height:400px;background-color:gray}mat-label:after{content:attr(data-after-content)}.map-container{position:relative}.map-container .address-incomplete{position:absolute;inset:0;background:#fff;border:1px solid #efefef;opacity:.87;font-size:larger;padding:40px;align-items:center;display:flex;flex-direction:column;justify-content:center;text-align:center}.map-container .center{position:absolute;right:5px;top:5px;z-index:5;background-color:#fff}.name,.address2,.address3,.street{width:100%;display:block}.city-region-zip-country{display:flex;flex-wrap:wrap}.city-region-zip-country .city,.city-region-zip-country .region,.city-region-zip-country .zip,.city-region-zip-country .country{flex:1 0 50%}.city-region-zip-country .city>*,.city-region-zip-country .region>*,.city-region-zip-country .zip>*,.city-region-zip-country .country>*{padding-right:5px;box-sizing:border-box;display:block}.city-region-zip-country:last-child>*{margin-right:0}.city-region-zip-country ::ng-deep .mat-form-field{width:100%}@media (max-width: 600px){.city-region-zip-country{flex-direction:column}}\n"], components: [{ type: i4.MatFormField, selector: "mat-form-field", inputs: ["color", "appearance", "hideRequiredMarker", "hintLabel", "floatLabel"], exportAs: ["matFormField"] }, { type: FsAddressAutocompleteComponent, selector: "fs-address-autocomplete", inputs: ["format", "readonly", "showClear", "suggestions", "config", "disabled", "required", "placeholder"], outputs: ["addressChange"] }, { type: FsAddressRegionComponent, selector: "fs-address-region", inputs: ["region", "disabled", "country", "label", "required", "regionCountryOrder", "countries"], outputs: ["regionChange"] }, { type: FsAddressCountryComponent, selector: "fs-address-country", inputs: ["disabled", "required", "excludeCountries", "countries", "placeholder"], outputs: ["selectionChange"] }, { type: i2.FsMapComponent, selector: "fs-map", inputs: ["address", "width", "height", "lat", "lng", "scrollwheel", "fullscreenControl", "streetViewControl", "zoomControl", "maxZoom", "zoom", "mapTypeControlOptions", "options"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.MatLabel, selector: "mat-label" }, { type: i8.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i9.FsFormRequiredDirective, selector: "[fsFormRequired],[ngModel][required]", inputs: ["fsFormRequired", "required", "fsFormRequiredMessage"] }, { type: i9.FsFormNoFsValidatorsDirective, selector: "[ngModel]:not([required]):not([fsFormRequired]):not([fsFormCompare]):not([fsFormDateRange]):not([fsFormEmail]):not([fsFormEmails]):not([fsFormFunction]):not([fsFormGreater]):not([fsFormInteger]):not([fsFormLesser]):not([fsFormMax]):not([fsFormMaxLength]):not([fsFormMin]):not([fsFormMinLength]):not([fsFormNumeric]):not([fsFormPattern]):not([fsFormPhone]):not([fsFormUrl]):not([validate])" }, { type: i1.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { type: i2.FsMapMarkerDirective, selector: "fs-map-marker", inputs: ["options", "lat", "lng", "label"], outputs: ["dragEnd"] }], viewProviders: [
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [[new Optional(), NgForm]],
        },
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address', viewProviders: [
                        {
                            provide: ControlContainer,
                            useFactory: controlContainerFactory,
                            deps: [[new Optional(), NgForm]],
                        },
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<mat-form-field\n    *ngIf=\"config.name.visible!==false\"\n    class=\"name\">\n  <mat-label>Location name</mat-label>\n  <input\n    matInput\n    autocomplete=\"nope\"\n    [(ngModel)]=\"address.name\"\n    [disabled]=\"config.name.disabled\"\n    [fsFormRequired]=\"config.name.required\"\n    [name]=\"controlNames.locationName\"    \n    (change)=\"change($event)\"\n    autocomplete=\"off\">\n</mat-form-field>\n\n<ng-container *ngIf=\"config.street.visible!==false\">\n  <ng-container *ngIf=\"suggestions; else textStreetField\">\n    <fs-address-autocomplete\n      [config]=\"config\"\n      [(ngModel)]=\"address\"\n      (ngModelChange)=\"change()\"\n      [disabled]=\"config.street.disabled\"\n      [suggestions]=\"suggestions\"\n      [showClear]=\"false\"\n      [placeholder]=\"config.street.placeholder || 'Street'\"\n      [name]=\"controlNames.street\">\n    </fs-address-autocomplete>\n  </ng-container>\n\n  <ng-template #textStreetField>\n    <mat-form-field class=\"street\">\n      <mat-label>{{config.street.placeholder || 'Street'}}</mat-label>\n      <input\n        matInput\n        type=\"search\"\n        autocomplete=\"nope\"\n        [(ngModel)]=\"address.street\"\n        [disabled]=\"config.street.disabled\"\n        [fsFormRequired]=\"config.street.required\"\n        [name]=\"controlNames.street\"\n        (change)=\"change()\"\n        autocomplete=\"off\">\n      </mat-form-field>\n  </ng-template>\n</ng-container>\n\n<mat-form-field\n    *ngIf=\"config.address2.visible\"\n    class=\"address2\">\n  <mat-label>{{config.address2.placeholder || 'Address 2'}}</mat-label>\n  <input\n    matInput\n    type=\"search\"\n    autocomplete=\"nope\"\n    [(ngModel)]=\"address.address2\"\n    [disabled]=\"config.address2.disabled\"\n    [fsFormRequired]=\"config.address2.required\"\n    [name]=\"controlNames.address2\"\n    (change)=\"change($event)\"\n    autocomplete=\"off\">\n</mat-form-field>\n\n<mat-form-field\n    *ngIf=\"config.address3.visible\"\n    class=\"address3\">\n  <mat-label>{{config.address3.placeholder || 'Address 3'}}</mat-label>\n  <input\n    matInput\n    type=\"search\"\n    autocomplete=\"nope\"\n    [(ngModel)]=\"address.address3\"\n    [disabled]=\"config.address3.disabled\"\n    [fsFormRequired]=\"config.address3.required\"\n    [name]=\"controlNames.address3\"\n    (change)=\"change($event)\"\n    autocomplete=\"off\">\n</mat-form-field>\n<div class=\"city-region-zip-country\" fxLayout.lt-sm=\"column\">\n  <div class=\"city\">\n    <mat-form-field *ngIf=\"config.city.visible!==false\">\n      <mat-label>{{config.city.placeholder || 'City'}}</mat-label>\n      <input\n        matInput\n        type=\"search\"\n        autocomplete=\"nope\"\n        [(ngModel)]=\"address.city\"\n        [disabled]=\"config.city.disabled\"\n        [fsFormRequired]=\"config.city.required\"\n        [name]=\"controlNames.city\"\n        (change)=\"change($event)\"\n        autocomplete=\"off\">\n    </mat-form-field>\n  </div>\n\n  <div class=\"region\">\n    <fs-address-region\n      *ngIf=\"config.region.visible!==false\"\n      [(region)]=\"address.region\"\n      (regionChange)=\"changeRegion()\"\n      [country]=\"address.country\"\n      [countries]=\"regionCountries\"\n      [label]=\"config.region.placeholder\"\n      [disabled]=\"config.region.disabled\"\n      [required]=\"config.region.required\"\n      [regionCountryOrder]=\"regionCountryOrder\">\n    </fs-address-region>\n  </div>\n\n  <div class=\"country\">\n    <fs-address-country\n      *ngIf=\"config.country.visible!==false\"\n      [disabled]=\"config.country.disabled\"\n      [required]=\"config.country.required\"\n      [placeholder]=\"config.country.placeholder\"\n      [excludeCountries]=\"excludeCountries\"\n      [(ngModel)]=\"address.country\"\n      (selectionChange)=\"changeCountry()\"\n      [name]=\"controlNames.addressCountry\">\n    </fs-address-country>\n  </div>\n\n  <div class=\"zip\">\n    <mat-form-field\n      *ngIf=\"config.zip.visible!==false\">\n      <mat-label>{{ zipLabel }}</mat-label>\n      <input\n        matInput\n        type=\"search\"\n        autocomplete=\"nope\"\n        [(ngModel)]=\"address.zip\"\n        [disabled]=\"config.zip.disabled\"\n        [fsFormRequired]=\"config.zip.required\"\n        [name]=\"controlNames.zip\"\n        (change)=\"change($event)\"\n        autocomplete=\"off\">\n    </mat-form-field>\n  </div>\n\n</div>\n\n<div class=\"map-container\" *ngIf=\"mapConfig.showMap\">\n  <fs-map\n      [height]=\"'300px'\"\n      [width]=\"'100%'\"\n      [lat]=\"mapConfig.center.latitude\"\n      [lng]=\"mapConfig.center.longitude\"\n      [zoom]=\"mapConfig.zoom\"\n      [scrollwheel]=\"mapConfig.scrollwheel\"\n      [streetViewControl]=\"mapConfig.streetViewControl\"\n      [zoomControl]=\"mapConfig.zoomControl\"\n      [mapTypeControlOptions]=\"mapConfig.mapTypeControlOptions\">\n    <fs-map-marker\n      [lat]=\"mapConfig.marker.coords.latitude\"\n      [lng]=\"mapConfig.marker.coords.longitude\">\n    </fs-map-marker>\n  </fs-map>\n\n  <div class=\"address-incomplete\" *ngIf=\"!address.lat && !address.lng\">\n    <div *ngIf=\"!isSearched\">Please populate the address above to locate it on the map</div>\n    <div *ngIf=\"isSearched\">Could not find address \"{{ searchedAddress }}\"</div>\n  </div>\n</div>\n", styles: ["agm-map{width:100%;height:400px;background-color:gray}mat-label:after{content:attr(data-after-content)}.map-container{position:relative}.map-container .address-incomplete{position:absolute;inset:0;background:#fff;border:1px solid #efefef;opacity:.87;font-size:larger;padding:40px;align-items:center;display:flex;flex-direction:column;justify-content:center;text-align:center}.map-container .center{position:absolute;right:5px;top:5px;z-index:5;background-color:#fff}.name,.address2,.address3,.street{width:100%;display:block}.city-region-zip-country{display:flex;flex-wrap:wrap}.city-region-zip-country .city,.city-region-zip-country .region,.city-region-zip-country .zip,.city-region-zip-country .country{flex:1 0 50%}.city-region-zip-country .city>*,.city-region-zip-country .region>*,.city-region-zip-country .zip>*,.city-region-zip-country .country>*{padding-right:5px;box-sizing:border-box;display:block}.city-region-zip-country:last-child>*{margin-right:0}.city-region-zip-country ::ng-deep .mat-form-field{width:100%}@media (max-width: 600px){.city-region-zip-country{flex-direction:column}}\n"] }]
        }], propDecorators: { fsAddressRegionComponent: [{
                type: ViewChild,
                args: [FsAddressRegionComponent]
            }], map: [{
                type: ViewChild,
                args: [FsMapComponent]
            }], address: [{
                type: Input
            }], excludeCountries: [{
                type: Input
            }], regionCountryOrder: [{
                type: Input
            }], suggestions: [{
                type: Input
            }], setConfig: [{
                type: Input,
                args: ['config']
            }], addressChange: [{
                type: Output
            }], collapseChange: [{
                type: Output
            }] } });

class FsAddressDialogComponent {
    constructor(_dialogRef, _data) {
        this._dialogRef = _dialogRef;
        this._data = _data;
        this.submit = () => {
            this._dialogRef.close(this.address);
            return of(true);
        };
        this.address = _data.address;
        this.config = _data.config;
    }
    get initialEditDialog() {
        return this._data.initial;
    }
    addressChange(address) {
        this.address = address;
    }
    recenter() {
        this.addressComponent.recenter();
    }
}
FsAddressDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressDialogComponent, deps: [{ token: i1$3.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
FsAddressDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressDialogComponent, selector: "ng-component", viewQueries: [{ propertyName: "addressComponent", first: true, predicate: FsAddressComponent, descendants: true }], ngImport: i0, template: "<form fsForm [submit]=\"submit\" [dirtySubmitButton]=\"!initialEditDialog\">\n  <fs-dialog>\n    <div mat-dialog-content>\n      <fs-address\n        [(address)]=\"address\"\n        [config]=\"config\"\n        (addressChange)=\"addressChange($event)\">\n      </fs-address>\n    </div>\n    <div mat-dialog-actions>\n      <button mat-button type=\"submit\" color=\"primary\" data-cy=\"dialog-apply\">Apply</button>\n      <button\n        mat-button\n        type=\"button\"\n        data-cy=\"center-address\"\n        *ngIf=\"config.map.showMap && address.lat && address.lng\"\n        (click)=\"recenter()\">\n        Center Address\n      </button>\n      <button mat-button type=\"button\" [mat-dialog-close]=\"null\" data-cy=\"dialog-cancel\">Cancel</button>\n    </div>\n  </fs-dialog>\n</form>\n", styles: [""], components: [{ type: i2$1.FsDialogComponent, selector: "fs-dialog", inputs: ["mobileMode", "mobileActionPlacement", "mobileWidth", "mode"] }, { type: FsAddressComponent, selector: "fs-address", inputs: ["address", "excludeCountries", "regionCountryOrder", "suggestions", "config"], outputs: ["addressChange", "collapseChange"] }, { type: i4$1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i1.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i1.NgForm, selector: "form:not([ngNoForm]):not([formGroup]),ng-form,[ngForm]", inputs: ["ngFormOptions"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i9.FsFormDirective, selector: "[fsForm]", inputs: ["wrapperSelector", "messageSelector", "hintSelector", "labelSelector", "autocomplete", "shortcuts", "confirm", "confirmDialog", "confirmDrawer", "confirmBrowser", "confirmTabs", "dirtySubmitButton", "submit", "successDelay", "errorDelay", "tabGroup", "deactivationGuard"], outputs: ["fsForm", "invalid", "valid", "submitted", "reseted", "cleared"] }, { type: i1$3.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: i1$3.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]" }, { type: i9.FsButtonDirective, selector: "[mat-raised-button],[mat-button],[mat-flat-button],[mat-stroked-button]", inputs: ["name", "dirtySubmit", "form"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$3.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["aria-label", "type", "mat-dialog-close", "matDialogClose"], exportAs: ["matDialogClose"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressDialogComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, template: "<form fsForm [submit]=\"submit\" [dirtySubmitButton]=\"!initialEditDialog\">\n  <fs-dialog>\n    <div mat-dialog-content>\n      <fs-address\n        [(address)]=\"address\"\n        [config]=\"config\"\n        (addressChange)=\"addressChange($event)\">\n      </fs-address>\n    </div>\n    <div mat-dialog-actions>\n      <button mat-button type=\"submit\" color=\"primary\" data-cy=\"dialog-apply\">Apply</button>\n      <button\n        mat-button\n        type=\"button\"\n        data-cy=\"center-address\"\n        *ngIf=\"config.map.showMap && address.lat && address.lng\"\n        (click)=\"recenter()\">\n        Center Address\n      </button>\n      <button mat-button type=\"button\" [mat-dialog-close]=\"null\" data-cy=\"dialog-cancel\">Cancel</button>\n    </div>\n  </fs-dialog>\n</form>\n", styles: [""] }]
        }], ctorParameters: function () { return [{ type: i1$3.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; }, propDecorators: { addressComponent: [{
                type: ViewChild,
                args: [FsAddressComponent]
            }] } });

class FsAddressFormatComponent {
    constructor() {
        this._address = {};
        this.format = AddressFormat.OneLine;
        this.disabled = false;
        this.name = true;
        this.lines = [];
        this.empty = false;
    }
    set address(address) {
        this._address = address;
        this._updateView();
    }
    get address() {
        return this._address;
    }
    ngOnInit() {
        this._updateView();
    }
    _updateView() {
        const address = {
            ...this.address,
            name: this.name ? this.address?.name : null,
        };
        if (this.format === AddressFormat.Summary) {
            const formatted = addressSummaryFormat(address);
            this.lines = formatted ? [formatted] : [];
        }
        else if (this.format === AddressFormat.OneLine) {
            const formatted = addressOneLineFormat(address, { includeFirst: this.includeFirst });
            this.lines = formatted ? [formatted] : [];
        }
        else if (this.format === AddressFormat.TwoLine) {
            this.lines = addressTwoLineFormat(address, { includeFirst: this.includeFirst });
        }
        this.empty = !this.lines.length;
    }
}
FsAddressFormatComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressFormatComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsAddressFormatComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressFormatComponent, selector: "fs-address-format", inputs: { address: "address", format: "format", includeFirst: "includeFirst", disabled: "disabled", name: "name" }, ngImport: i0, template: "<span *ngIf=\"!empty\" [ngClass]=\"format\" [class.disabled]=\"disabled\">\n  <span *ngFor=\"let parts of lines; let index = index\" class=\"line-{{index + 1}}\">\n    <span *ngFor=\"let part of parts;\" class=\"part\">{{part}}</span>\n  </span>\n</span>\n\n\n", styles: [".twoline .line-1{display:block}.twoline .line-2{font-size:smaller}.part+.part:before{content:\", \"}.part:last-child:after{content:\"\"}.disabled{color:#00000061}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressFormatComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address-format', changeDetection: ChangeDetectionStrategy.OnPush, template: "<span *ngIf=\"!empty\" [ngClass]=\"format\" [class.disabled]=\"disabled\">\n  <span *ngFor=\"let parts of lines; let index = index\" class=\"line-{{index + 1}}\">\n    <span *ngFor=\"let part of parts;\" class=\"part\">{{part}}</span>\n  </span>\n</span>\n\n\n", styles: [".twoline .line-1{display:block}.twoline .line-2{font-size:smaller}.part+.part:before{content:\", \"}.part:last-child:after{content:\"\"}.disabled{color:#00000061}\n"] }]
        }], propDecorators: { address: [{
                type: Input
            }], format: [{
                type: Input
            }], includeFirst: [{
                type: Input
            }], disabled: [{
                type: Input
            }], name: [{
                type: Input
            }] } });

class FsAddressSearchComponent {
    constructor() {
        this.name = true;
        this.address = {};
        this.format = AddressFormat.TwoLine;
        this.disabled = false;
        this.readonly = false;
        this.required = false;
        this.cleared = new EventEmitter();
        this.addressChange = new EventEmitter();
        this.edit = new EventEmitter();
        this.selected = new EventEmitter();
        this.autocompleteName = `search-${guid('xxxxxxxx')}`;
        this._destroy$ = new Subject();
        this._config = {};
    }
    set config(value) {
        this._config = value;
        if (this._config) {
            this.required =
                ((this.config.name && this.config.name.required) ||
                    (this.config.country && this.config.country.required) ||
                    (this.config.region && this.config.region.required) ||
                    (this.config.city && this.config.city.required) ||
                    (this.config.street && this.config.street.required) ||
                    (this.config.address2 && this.config.address2.required) ||
                    (this.config.address3 && this.config.address3.required) ||
                    (this.config.zip && this.config.zip.required));
        }
    }
    get config() {
        return this._config;
    }
    get editable() {
        return !this.disabled && !this.readonly;
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    clear() {
        this.address = createEmptyAddress();
        this.cleared.emit(createEmptyAddress());
        this.addressChange.emit(createEmptyAddress());
        this.autocomplete.clear();
    }
    resetAutocomplete() {
        this.autocomplete.reset();
    }
    editClick() {
        if (this.editable) {
            this.edit.emit();
        }
    }
    addressSelected() {
        this.selected.emit(this.address);
        this.addressChange.emit(this.address);
    }
}
FsAddressSearchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressSearchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsAddressSearchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressSearchComponent, selector: "fs-address-search", inputs: { config: "config", name: "name", address: "address", format: "format", disabled: "disabled", readonly: "readonly", required: "required" }, outputs: { cleared: "cleared", addressChange: "addressChange", edit: "edit", selected: "selected" }, viewQueries: [{ propertyName: "autocomplete", first: true, predicate: FsAddressAutocompleteComponent, descendants: true }], ngImport: i0, template: "<fs-address-autocomplete\n    [config]=\"config\"\n    [(ngModel)]=\"address\"\n    [disabled]=\"disabled\"\n    (addressChange)=\"addressSelected()\"\n    [ngClass]=\"{ hide: !addressControl.empty }\"\n    [placeholder]=\"config.label\"\n    [name]=\"autocompleteName\"\n    [required]=\"required\"\n    #addressControl>\n  <fs-address-format\n    [address]=\"address\"\n    [format]=\"format\"\n    [name]=\"name\"\n    (click)=\"editClick()\"\n    [ngClass]=\"{ editable: editable }\"\n    [disabled]=\"disabled\">\n  </fs-address-format>\n</fs-address-autocomplete>\n", styles: [":host ::ng-deep fs-address-format{display:block}:host ::ng-deep fs-address-format.editable{cursor:pointer}:host ::ng-deep mat-form-field{width:100%}:host ::ng-deep .hide input{visibility:hidden;position:absolute}\n"], components: [{ type: FsAddressAutocompleteComponent, selector: "fs-address-autocomplete", inputs: ["format", "readonly", "showClear", "suggestions", "config", "disabled", "required", "placeholder"], outputs: ["addressChange"] }, { type: FsAddressFormatComponent, selector: "fs-address-format", inputs: ["address", "format", "includeFirst", "disabled", "name"] }], directives: [{ type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i9.FsFormRequiredDirective, selector: "[fsFormRequired],[ngModel][required]", inputs: ["fsFormRequired", "required", "fsFormRequiredMessage"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i1.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }], viewProviders: [
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [[new Optional(), NgForm]],
        }
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address-search', viewProviders: [
                        {
                            provide: ControlContainer,
                            useFactory: controlContainerFactory,
                            deps: [[new Optional(), NgForm]],
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<fs-address-autocomplete\n    [config]=\"config\"\n    [(ngModel)]=\"address\"\n    [disabled]=\"disabled\"\n    (addressChange)=\"addressSelected()\"\n    [ngClass]=\"{ hide: !addressControl.empty }\"\n    [placeholder]=\"config.label\"\n    [name]=\"autocompleteName\"\n    [required]=\"required\"\n    #addressControl>\n  <fs-address-format\n    [address]=\"address\"\n    [format]=\"format\"\n    [name]=\"name\"\n    (click)=\"editClick()\"\n    [ngClass]=\"{ editable: editable }\"\n    [disabled]=\"disabled\">\n  </fs-address-format>\n</fs-address-autocomplete>\n", styles: [":host ::ng-deep fs-address-format{display:block}:host ::ng-deep fs-address-format.editable{cursor:pointer}:host ::ng-deep mat-form-field{width:100%}:host ::ng-deep .hide input{visibility:hidden;position:absolute}\n"] }]
        }], propDecorators: { config: [{
                type: Input
            }], name: [{
                type: Input
            }], address: [{
                type: Input
            }], format: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], required: [{
                type: Input
            }], cleared: [{
                type: Output
            }], addressChange: [{
                type: Output
            }], edit: [{
                type: Output
            }], selected: [{
                type: Output
            }], autocomplete: [{
                type: ViewChild,
                args: [FsAddressAutocompleteComponent]
            }] } });

class FsAddressPickerComponent {
    constructor(_dialog, _cdRef) {
        this._dialog = _dialog;
        this._cdRef = _cdRef;
        this.addressChange = new EventEmitter();
        this.name = true;
        this.view = 'search';
        this.config = {};
        this._destroy$ = new Subject();
    }
    set setConfig(config) {
        config = cloneDeep(config);
        if (!config.format) {
            config.format = AddressFormat.TwoLine;
        }
        config.search = true;
        if (!isObject(config.map)) {
            config.map = { showMap: false };
        }
        config.disabled = this.config.disabled;
        config.readonly = this.config.readonly;
        this.config = config;
    }
    set setFormat(value) {
        this.config.format = value;
    }
    set setDisabled(value) {
        this.config.disabled = value;
    }
    set setReadonly(value) {
        this.config.readonly = value;
    }
    ngOnChanges(changes) {
        if (changes.address
            && changes.address.currentValue !== changes.address.previousValue) {
            if (!this.address) {
                this.address = createEmptyAddress();
            }
        }
    }
    addressEdit() {
        this.open({ value: this.address, initialChange: false });
    }
    addressSelected(address) {
        if (this.config.confirmation || address.manual) {
            this.open({ value: address, initialChange: true })
                .afterClosed()
                .pipe(takeUntil(this._destroy$))
                .subscribe((result) => {
                if (!result) {
                    this.addressSearch.clear();
                }
            });
        }
        else {
            this.addressChange.emit(address);
        }
    }
    open(event) {
        const dialogRef = this._dialog.open(FsAddressDialogComponent, {
            width: '700px',
            data: {
                address: event.value || this.address,
                config: this.config,
                initial: event.initialChange,
            }
        });
        dialogRef.afterClosed()
            .pipe(filter((result) => !!result), takeUntil(this._destroy$))
            .subscribe((result) => {
            this.address = result;
            // hard dirty fix for DT-T867.
            // In future it must be ControlValue Accessor...
            if (result) {
                this.search.autocomplete.value = this.address;
            }
            this.addressChange.emit(this.address);
            this._cdRef.markForCheck();
        });
        return dialogRef;
    }
    searchEdited(event) {
        this.open(event);
    }
    clear() {
        this.address = {};
        this.addressSearch.clear();
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
FsAddressPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressPickerComponent, deps: [{ token: i1$3.MatDialog }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsAddressPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressPickerComponent, selector: "fs-address-picker", inputs: { setConfig: ["config", "setConfig"], setFormat: ["format", "setFormat"], setDisabled: ["disabled", "setDisabled"], setReadonly: ["readonly", "setReadonly"], address: "address", name: "name" }, outputs: { addressChange: "addressChange" }, viewQueries: [{ propertyName: "addressSearch", first: true, predicate: FsAddressSearchComponent, descendants: true, static: true }, { propertyName: "search", first: true, predicate: FsAddressSearchComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"address-picker-container\">\n  <div class=\"search\">\n    <fs-address-search\n      [address]=\"address\"\n      [config]=\"config\"\n      [format]=\"config.format\"\n      [disabled]=\"config.disabled\"\n      [readonly]=\"config.readonly\"\n      [name]=\"name\"\n      (edit)=\"addressEdit()\"\n      (addressChange)=\"addressChange.emit($event)\"\n      (selected)=\"addressSelected($event)\">\n    </fs-address-search>\n  </div>\n</div>\n", styles: [".address-picker-container{position:relative}.input-controls{position:absolute;top:0;right:0}:host ::ng-deep .search .mat-form-field-flex{flex-direction:row}:host ::ng-deep .search .mat-form-field-suffix{align-self:center}:host ::ng-deep .hide-underline .mat-form-field-underline{display:none}:host ::ng-deep .hide-underline .mat-form-field-subscript-wrapper{margin-top:0}.map-buttons{margin-top:5px}.map-buttons button{width:100%}\n"], components: [{ type: FsAddressSearchComponent, selector: "fs-address-search", inputs: ["config", "name", "address", "format", "disabled", "readonly", "required"], outputs: ["cleared", "addressChange", "edit", "selected"] }], viewProviders: [
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [[new Optional(), NgForm]],
        }
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address-picker', changeDetection: ChangeDetectionStrategy.OnPush, viewProviders: [
                        {
                            provide: ControlContainer,
                            useFactory: controlContainerFactory,
                            deps: [[new Optional(), NgForm]],
                        }
                    ], template: "<div class=\"address-picker-container\">\n  <div class=\"search\">\n    <fs-address-search\n      [address]=\"address\"\n      [config]=\"config\"\n      [format]=\"config.format\"\n      [disabled]=\"config.disabled\"\n      [readonly]=\"config.readonly\"\n      [name]=\"name\"\n      (edit)=\"addressEdit()\"\n      (addressChange)=\"addressChange.emit($event)\"\n      (selected)=\"addressSelected($event)\">\n    </fs-address-search>\n  </div>\n</div>\n", styles: [".address-picker-container{position:relative}.input-controls{position:absolute;top:0;right:0}:host ::ng-deep .search .mat-form-field-flex{flex-direction:row}:host ::ng-deep .search .mat-form-field-suffix{align-self:center}:host ::ng-deep .hide-underline .mat-form-field-underline{display:none}:host ::ng-deep .hide-underline .mat-form-field-subscript-wrapper{margin-top:0}.map-buttons{margin-top:5px}.map-buttons button{width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$3.MatDialog }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { addressSearch: [{
                type: ViewChild,
                args: [FsAddressSearchComponent, { static: true }]
            }], setConfig: [{
                type: Input,
                args: ['config']
            }], setFormat: [{
                type: Input,
                args: ['format']
            }], setDisabled: [{
                type: Input,
                args: ['disabled']
            }], setReadonly: [{
                type: Input,
                args: ['readonly']
            }], address: [{
                type: Input
            }], addressChange: [{
                type: Output
            }], name: [{
                type: Input
            }], search: [{
                type: ViewChild,
                args: [FsAddressSearchComponent]
            }] } });

const COUNTRIES = new InjectionToken('fs-address-country.countries');

class FsAddressCountriesModule {
}
FsAddressCountriesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountriesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsAddressCountriesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountriesModule });
FsAddressCountriesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountriesModule, providers: [
        { provide: COUNTRIES, useValue: Countries }
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountriesModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        { provide: COUNTRIES, useValue: Countries }
                    ]
                }]
        }] });

class FsAddressCountryModule {
}
FsAddressCountryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsAddressCountryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountryModule, declarations: [FsAddressCountryComponent], imports: [CommonModule,
        FormsModule,
        FsFormModule,
        FsAddressCountriesModule,
        FsAutocompleteModule], exports: [FsAddressCountryComponent] });
FsAddressCountryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountryModule, imports: [[
            CommonModule,
            FormsModule,
            FsFormModule,
            FsAddressCountriesModule,
            FsAutocompleteModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressCountryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        FsFormModule,
                        FsAddressCountriesModule,
                        FsAutocompleteModule,
                    ],
                    declarations: [
                        FsAddressCountryComponent
                    ],
                    exports: [
                        FsAddressCountryComponent
                    ]
                }]
        }] });

class FsAddressRegionModule {
}
FsAddressRegionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsAddressRegionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionModule, declarations: [FsAddressRegionComponent], imports: [CommonModule,
        FormsModule,
        FsFormModule,
        MatInputModule,
        FsAddressCountriesModule,
        FsAutocompleteModule], exports: [FsAddressRegionComponent] });
FsAddressRegionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionModule, imports: [[
            CommonModule,
            FormsModule,
            FsFormModule,
            MatInputModule,
            FsAddressCountriesModule,
            FsAutocompleteModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        FsFormModule,
                        MatInputModule,
                        FsAddressCountriesModule,
                        FsAutocompleteModule,
                    ],
                    declarations: [
                        FsAddressRegionComponent
                    ],
                    exports: [
                        FsAddressRegionComponent
                    ]
                }]
        }] });

class FsAddressModule {
    static forRoot() {
        return {
            ngModule: FsAddressModule,
        };
    }
}
FsAddressModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsAddressModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressModule, declarations: [FsAddressComponent,
        FsAddressFormatComponent,
        FsAddressPickerComponent,
        FsAddressSearchComponent,
        FsAddressDialogComponent,
        FsAddressAutocompleteComponent], imports: [CommonModule,
        FormsModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatDialogModule,
        FsFormModule,
        FsAddressCountriesModule,
        FsDialogModule,
        FsAddressRegionModule,
        FsAddressCountryModule,
        FsMapModule,
        FsClearModule], exports: [FsAddressComponent,
        FsAddressFormatComponent,
        FsAddressPickerComponent,
        FsAddressSearchComponent,
        FsAddressAutocompleteComponent] });
FsAddressModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressModule, imports: [[
            CommonModule,
            FormsModule,
            MatAutocompleteModule,
            MatButtonModule,
            MatIconModule,
            MatInputModule,
            MatSelectModule,
            MatDialogModule,
            FsFormModule,
            FsAddressCountriesModule,
            FsDialogModule,
            FsAddressRegionModule,
            FsAddressCountryModule,
            FsMapModule,
            FsClearModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        MatAutocompleteModule,
                        MatButtonModule,
                        MatIconModule,
                        MatInputModule,
                        MatSelectModule,
                        MatDialogModule,
                        FsFormModule,
                        FsAddressCountriesModule,
                        FsDialogModule,
                        FsAddressRegionModule,
                        FsAddressCountryModule,
                        FsMapModule,
                        FsClearModule,
                    ],
                    exports: [
                        FsAddressComponent,
                        FsAddressFormatComponent,
                        FsAddressPickerComponent,
                        FsAddressSearchComponent,
                        FsAddressAutocompleteComponent,
                    ],
                    declarations: [
                        FsAddressComponent,
                        FsAddressFormatComponent,
                        FsAddressPickerComponent,
                        FsAddressSearchComponent,
                        FsAddressDialogComponent,
                        FsAddressAutocompleteComponent,
                    ],
                }]
        }] });

class FsAddressRegionCountryComponent {
    constructor() {
        this.orientationVertical = true;
        this.orientationHorizontal = false;
        this.orientationHorizontalStretch = false;
        this.config = {};
        this.countryChange = new EventEmitter();
        this.regionChange = new EventEmitter();
        this.countries = Countries;
    }
    set setOrientation(value) {
        this.orientationVertical = value === 'vertical';
        this.orientationHorizontal = value === 'horizontal';
        this.orientationHorizontalStretch = value === 'horizontal-stretch';
    }
    ngOnInit() {
        this._initConfig();
    }
    changeCountry() {
        this.regionCountries = this.country ? [this.country] : null;
        if (!this.country) {
            this.region = null;
        }
        this.regionChange.emit(this.region);
        this.countryChange.emit(this.country);
    }
    changeRegion() {
        if (!!this.region) {
            const regionCountry = this.countries.find((country) => {
                return country.regions
                    && country.regions.find((region) => this.region === region.code);
            });
            if (regionCountry) {
                this.country = regionCountry.code;
                this.changeCountry();
            }
        }
        this.regionChange.emit(this.region);
    }
    _initConfig() {
        this.config = {
            country: { required: false },
            region: { required: false }, ...this.config,
        };
        if (this.config.country.list) {
            this.countries = Countries.filter((country) => {
                return this.config.country.list.indexOf(country.code) >= 0;
            });
        }
    }
}
FsAddressRegionCountryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionCountryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsAddressRegionCountryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsAddressRegionCountryComponent, selector: "fs-address-region-country", inputs: { config: "config", country: "country", region: "region", setOrientation: ["orientation", "setOrientation"] }, outputs: { countryChange: "countryChange", regionChange: "regionChange" }, host: { properties: { "class.vertical": "this.orientationVertical", "class.horizontal": "this.orientationHorizontal", "class.horizontal-stretch": "this.orientationHorizontalStretch" } }, ngImport: i0, template: "<div class=\"region-country\">\n  <div class=\"country\">\n    <fs-address-country\n      *ngIf=\"config.country.visible!==false\"\n      [disabled]=\"config.country.disabled\"\n      [required]=\"config.country.required\"\n      [placeholder]=\"config.country.placeholder\"\n      [countries]=\"countries\"\n      [(ngModel)]=\"country\"\n      (selectionChange)=\"changeCountry()\"\n      name=\"country\">\n    </fs-address-country>\n  </div>\n  <div class=\"region\">\n    <fs-address-region\n        [(region)]=\"region\"\n        [country]=\"country\"\n        (regionChange)=\"changeRegion()\"\n        [countries]=\"regionCountries\"\n        [disabled]=\"config.region.disabled\"\n        [required]=\"config.region.required\"\n        name=\"region\">\n    </fs-address-region>\n  </div>\n</div>", styles: [".region-country{display:flex;flex-direction:column}:host.horizontal .region-country{flex-direction:row}:host.horizontal ::ng-deep .region{padding-left:10px}:host.horizontal ::ng-deep fs-address-region .mat-form-field{width:100%}:host.vertical ::ng-deep fs-address-country .mat-form-field,:host.vertical ::ng-deep fs-address-region .mat-form-field{width:100%}:host.horizontal-stretch .region-country{display:grid;grid-template-columns:1fr 1fr;grid-gap:10px}:host.horizontal-stretch ::ng-deep fs-address-region .mat-form-field{width:100%}:host.horizontal-stretch ::ng-deep .country .mat-form-field{width:100%}\n"], components: [{ type: FsAddressCountryComponent, selector: "fs-address-country", inputs: ["disabled", "required", "excludeCountries", "countries", "placeholder"], outputs: ["selectionChange"] }, { type: FsAddressRegionComponent, selector: "fs-address-region", inputs: ["region", "disabled", "country", "label", "required", "regionCountryOrder", "countries"], outputs: ["regionChange"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i9.FsFormRequiredDirective, selector: "[fsFormRequired],[ngModel][required]", inputs: ["fsFormRequired", "required", "fsFormRequiredMessage"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionCountryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-address-region-country', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"region-country\">\n  <div class=\"country\">\n    <fs-address-country\n      *ngIf=\"config.country.visible!==false\"\n      [disabled]=\"config.country.disabled\"\n      [required]=\"config.country.required\"\n      [placeholder]=\"config.country.placeholder\"\n      [countries]=\"countries\"\n      [(ngModel)]=\"country\"\n      (selectionChange)=\"changeCountry()\"\n      name=\"country\">\n    </fs-address-country>\n  </div>\n  <div class=\"region\">\n    <fs-address-region\n        [(region)]=\"region\"\n        [country]=\"country\"\n        (regionChange)=\"changeRegion()\"\n        [countries]=\"regionCountries\"\n        [disabled]=\"config.region.disabled\"\n        [required]=\"config.region.required\"\n        name=\"region\">\n    </fs-address-region>\n  </div>\n</div>", styles: [".region-country{display:flex;flex-direction:column}:host.horizontal .region-country{flex-direction:row}:host.horizontal ::ng-deep .region{padding-left:10px}:host.horizontal ::ng-deep fs-address-region .mat-form-field{width:100%}:host.vertical ::ng-deep fs-address-country .mat-form-field,:host.vertical ::ng-deep fs-address-region .mat-form-field{width:100%}:host.horizontal-stretch .region-country{display:grid;grid-template-columns:1fr 1fr;grid-gap:10px}:host.horizontal-stretch ::ng-deep fs-address-region .mat-form-field{width:100%}:host.horizontal-stretch ::ng-deep .country .mat-form-field{width:100%}\n"] }]
        }], propDecorators: { orientationVertical: [{
                type: HostBinding,
                args: ['class.vertical']
            }], orientationHorizontal: [{
                type: HostBinding,
                args: ['class.horizontal']
            }], orientationHorizontalStretch: [{
                type: HostBinding,
                args: ['class.horizontal-stretch']
            }], config: [{
                type: Input
            }], country: [{
                type: Input
            }], region: [{
                type: Input
            }], setOrientation: [{
                type: Input,
                args: ['orientation']
            }], countryChange: [{
                type: Output
            }], regionChange: [{
                type: Output
            }] } });

class FsAddressRegionCountryModule {
}
FsAddressRegionCountryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionCountryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsAddressRegionCountryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionCountryModule, declarations: [FsAddressRegionCountryComponent], imports: [CommonModule,
        FormsModule,
        FsFormModule,
        MatInputModule,
        MatSelectModule,
        FsAddressCountriesModule,
        FsAddressRegionModule,
        FsAddressCountryModule], exports: [FsAddressRegionCountryComponent] });
FsAddressRegionCountryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionCountryModule, imports: [[
            CommonModule,
            FormsModule,
            FsFormModule,
            MatInputModule,
            MatSelectModule,
            FsAddressCountriesModule,
            FsAddressRegionModule,
            FsAddressCountryModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressRegionCountryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        FsFormModule,
                        MatInputModule,
                        MatSelectModule,
                        FsAddressCountriesModule,
                        FsAddressRegionModule,
                        FsAddressCountryModule
                    ],
                    declarations: [
                        FsAddressRegionCountryComponent
                    ],
                    exports: [
                        FsAddressRegionCountryComponent
                    ]
                }]
        }] });

const GOOGLE_MAP_KEY = new InjectionToken('fs-address-country.google-map-key');

class FsAddressGeocoder {
    constructor(_map) {
        this._map = _map;
    }
    lookup(address) {
        return new Observable((observer) => {
            this._map.loaded$
                .subscribe(() => {
                const geocoder = new google.maps.Geocoder();
                const request = {
                    address,
                };
                geocoder.geocode(request, (results, status) => {
                    if (status == google.maps.GeocoderStatus.OK) {
                        observer.next(results);
                        observer.complete();
                    }
                    else if (status == google.maps.GeocoderStatus.ZERO_RESULTS) {
                        observer.error("Bad destination address.");
                    }
                    else {
                        observer.error("Error calling Google Geocode API.");
                    }
                });
            });
        });
    }
}
FsAddressGeocoder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressGeocoder, deps: [{ token: i2.FsMap }], target: i0.ɵɵFactoryTarget.Injectable });
FsAddressGeocoder.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressGeocoder, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsAddressGeocoder, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2.FsMap }]; } });

// Modules

/**
 * Generated bundle index. Do not edit.
 */

export { AddressFormat, COUNTRIES, Countries, Country, FsAddressAutocompleteComponent, FsAddressComponent, FsAddressCountriesModule, FsAddressCountryComponent, FsAddressCountryModule, FsAddressFormatComponent, FsAddressGeocoder, FsAddressModule, FsAddressPickerComponent, FsAddressRegionComponent, FsAddressRegionCountryComponent, FsAddressRegionCountryModule, FsAddressRegionModule, FsAddressSearchComponent, GOOGLE_MAP_KEY, addressFormat, addressIsEmpty, addressOneLineFormat, addressSummaryFormat, addressTwoLineFormat };
